import axios from 'axios';
import {
    CONVERSATION_CLUSTER_DATA
} from '@/assets/dashboard/cache-keys';
import PusherRepo from '@/services/PusherRepo';
import { PUSHER_ID } from '@/config';
import {
    conversationClusterSocketOptions,
    conversationClusterChannelName,
    CC_ON_DEMAND_SEARCH_EVENT
} from '@/helpers/pusher';

const ON_DEMAND_SEARCHES_URL = '/v1/dashboard/conversation_clusters/on_demand_searches';

// One Pusher connection for every followed filter; each channel counts its followers so the last one unsubscribes
let pusherRepo = null;
const channelFollowers = {};

export const state = () => ({
    data: {},
    isLoading: false,
    searchPhrase: '',
    initialOnDemandSearchId: null,
    onDemandSearchStatuses: {}
});

export const getters = {
    DATA_KEY: () => CONVERSATION_CLUSTER_DATA,
    data: state => state.data,
    isLoading: state => state.isLoading,
    searchPhrase: state => state.searchPhrase,
    initialOnDemandSearchId: state => state.initialOnDemandSearchId,
    onDemandSearchStatus: state => id => state.onDemandSearchStatuses[id] ?? null,
    areRecentRunsDisabled: (state, getters, rootState, rootGetters) =>
        !!rootGetters['auth/loggedUser']?.conversationClusterRecentRunsDisabled
};

export const mutations = {
    SET_CONVERSATION_CLUSTER(state, {id, data}) {
        state.data[id] = data
    },

    UPDATE_SEARCH_PHRASE(state, { searchPhrase }) {
        state.searchPhrase = searchPhrase
    },

    UPDATE_LOADING_STATUS(state, { status }) {
        state.isLoading = status
    },

    SET_INITIAL_ON_DEMAND_SEARCH_ID(state, id) {
        state.initialOnDemandSearchId = id;
    },

    SET_ON_DEMAND_SEARCH_STATUS(state, { id, status }) {
        state.onDemandSearchStatuses = { ...state.onDemandSearchStatuses, [id]: status };
    }
};

export const actions = {
    async index(vuexContext, { accountId, filterId, range }) {
        const params = {
            range,
            accountId,
            filterId
        };
        let path = '/v1/dashboard/conversation_clusters/searches';

        const res = await axios.getMethodOverridePost(path, { params, encodeURI: true });

        vuexContext.commit('SET_CONVERSATION_CLUSTER', {id: filterId, data: res});

        return res;
    },

    setSearchPhrase(vuexContext, { searchPhrase }) {
        vuexContext.commit('UPDATE_SEARCH_PHRASE', { searchPhrase: searchPhrase });
    },

    setInitialOnDemandSearchId(vuexContext, id) {
        vuexContext.commit('SET_INITIAL_ON_DEMAND_SEARCH_ID', id);
    },

    clearInitialOnDemandSearchId(vuexContext) {
        vuexContext.commit('SET_INITIAL_ON_DEMAND_SEARCH_ID', null);
    },

    fetchOnDemandSearches(vuexContext, { filterId, accountId }) {
        return axios.getMethodOverridePost(ON_DEMAND_SEARCHES_URL, { params: { filterId, accountId }, encodeURI: true });
    },

    validateOnDemandSearch(vuexContext, payload) {
        return axios.$post(`${ON_DEMAND_SEARCHES_URL}/validate`, payload);
    },

    createOnDemandSearch(vuexContext, payload) {
        return axios.$post(ON_DEMAND_SEARCHES_URL, payload);
    },

    followOnDemandSearches(vuexContext, { filterId }) {
        const name = conversationClusterChannelName(filterId);

        if (!pusherRepo) {
            pusherRepo = new PusherRepo(PUSHER_ID, conversationClusterSocketOptions());
        }

        if (!channelFollowers[name]) {
            pusherRepo.subscribe(name).bind(CC_ON_DEMAND_SEARCH_EVENT, ({ message }) => {
                vuexContext.commit('SET_ON_DEMAND_SEARCH_STATUS', message);
            });
        }

        channelFollowers[name] = (channelFollowers[name] ?? 0) + 1;
    },

    unfollowOnDemandSearches(vuexContext, { filterId }) {
        const name = conversationClusterChannelName(filterId);

        channelFollowers[name] -= 1;

        if (channelFollowers[name] === 0) {
            pusherRepo.unsubscribe(name);
            delete channelFollowers[name];
        }
    },

    async get(vuexContext, { id, timeZone }) {
        vuexContext.commit('UPDATE_LOADING_STATUS', { status: true });

        const params = {
            timeZone
        }

        let path = `/v1/dashboard/conversation_clusters/searches/${id}`;

        const res = await axios.$get(path, {params});

        vuexContext.commit('UPDATE_LOADING_STATUS', { status: false });

        return res;
    },

    async getCoreStatuses(vuexContext, { searchId, clusterId }) {
        const res = await axios.$get(`/v1/dashboard/conversation_clusters/searches/${searchId}/clusters/${clusterId}/core_statuses`);

        return res.data;
    },

    async getCsv(vuexContext, { id, onlyMeaningful, timeZone }) {
        const params = {
            timeZone
        }

        if(onlyMeaningful) params.only_meaningful = onlyMeaningful;
        let path = `/v1/dashboard/conversation_clusters/searches/${id}.csv`;

        const res = await axios.$get(path, {params});

        return res;
    }
};
